// Informes por puesto (lote D, M49): lógica pura, sin base de datos.
//  - Contenido congelado del informe de un puesto, su SHA-256 (forma canónica de src/lib/firmas, la misma del lote B)
//    y la huella de las evaluaciones del puesto (si cambia después de aprobar, el informe queda vencido).
//  - Vigencia: 12 meses desde la aprobación o hasta que cambie una evaluación del puesto (sugerido por StorSafe, no
//    normativo; lo valida Kevin).
//  - GT 7: medidas agrupadas por nivel de la jerarquía de controles, sin duplicar: las de la Matriz de Riesgos y de
//    Incidentes se leen de su origen.
//  - Versiones: una aprobada no se edita (la base tampoco lo deja); para cambiar algo se aprueba una versión nueva.

import { canonico } from '../firmas/firmas-logica';
import { diaEc } from '../fechas';
import { sha256Hex } from '../psicosocial-codigos';
import {
  COLUMNA_IPER, DIAS_TOLERANCIA, MESES_VIGENCIA, MODULOS_DE_TIPO, ORDEN_JERARQUIA, type AdjuntoContenido, type ContenidoInforme,
  type Evaluacion, type FilaAdjunto, type FilaInforme, type FilaMedida, type FilaVersion, type MedidaVista, type NivelControl, type TipoInforme,
} from './ip-tipos';

// ── Fechas (en días calendario, sin horas) ──
export const isoDia = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// Un instante (approved_at) se pasa antes al día de Ecuador; una fecha sin hora queda igual (lib/fechas).
export function sumarMesesIso(iso: string, meses: number): string {
  const [a, m, d] = diaEc(iso).split('-').map(Number);
  const f = new Date(Date.UTC(a, m - 1 + meses, d));
  // 31 de un mes sin 31: el último día del mes destino.
  if (f.getUTCDate() !== d) f.setUTCDate(0);
  return f.toISOString().slice(0, 10);
}
export function diasEntre(desdeIso: string, hastaIso: string): number {
  const [a1, m1, d1] = diaEc(desdeIso).split('-').map(Number);
  const [a2, m2, d2] = diaEc(hastaIso).split('-').map(Number);
  return Math.round((Date.UTC(a2, m2 - 1, d2) - Date.UTC(a1, m1 - 1, d1)) / 86400000);
}
/** Fecha local de Ecuador de una marca de tiempo del servidor. */
export const diaEcuador = (ts: string) => new Date(ts).toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' });

// ── Evaluaciones del informe ──
export function evaluacionesDeTipo(tipo: TipoInforme, todas: Evaluacion[]): Evaluacion[] {
  const mods = tipo === 'medidas' ? null : MODULOS_DE_TIPO[tipo];
  return todas.filter(e => !mods || mods.includes(e.modulo))
    .sort((a, b) => a.modulo.localeCompare(b.modulo) || b.fecha.localeCompare(a.fecha) || a.id.localeCompare(b.id));
}

/** Huella de las evaluaciones: cambia si se agrega, quita o cambia el resultado de alguna. */
export function textoHuella(evals: Evaluacion[]): string {
  return canonico([...evals].sort((a, b) => a.modulo.localeCompare(b.modulo) || a.id.localeCompare(b.id))
    .map(e => ({ m: e.modulo, id: e.id, f: e.fecha, r: e.resultado, c: e.cumple })));
}
export const huellaEvaluaciones = (evals: Evaluacion[]) => sha256Hex(textoHuella(evals));
export const hashInforme = (c: ContenidoInforme) => sha256Hex(canonico(c));

// ── Medidas (GT 7) ──
export interface IperConMedidas { id: string; position_id: string | null; task?: string | null; hazard_type?: string | null;
  medida_eliminacion?: string | null; medida_sustitucion?: string | null; medida_control_ingenieria?: string | null;
  medida_control_administrativo?: string | null; medida_epp?: string | null }
export interface AccionIncidente { id: string; description: string; control_level: string | null; responsible_name: string | null;
  due_date: string | null; follow_up: string | null; status: string | null; completed_at: string | null; closed_at?: string | null;
  evidence_name: string | null; evidence_path: string | null; position_id: string | null }

/** Lo que se puede vincular al puesto: cada medida_* no vacía de sus evaluaciones IPER y las medidas de sus incidentes. */
export function candidatosMedidas(puestoId: string, iper: IperConMedidas[], acciones: AccionIncidente[], vinculadas: FilaMedida[]) {
  const ya = new Set(vinculadas.map(m => m.source === 'iper' ? `iper:${m.iper_assessment_id}:${m.control_level}` : `inc:${m.incident_action_id}`));
  const deIper = iper.filter(i => i.position_id === puestoId).flatMap(i => ORDEN_JERARQUIA.map(nivel => {
    const texto = String((i as unknown as Record<string, unknown>)[COLUMNA_IPER[nivel]] ?? '').trim();
    return texto ? { origen: 'iper' as const, iper_id: i.id, nivel, texto, de: [i.task, i.hazard_type].filter(Boolean).join(' · ') } : null;
  })).filter((x): x is NonNullable<typeof x> => !!x && !ya.has(`iper:${x.iper_id}:${x.nivel}`));
  const deIncidentes = acciones.filter(a => a.position_id === puestoId && !ya.has(`inc:${a.id}`))
    .map(a => ({ origen: 'incidente' as const, accion_id: a.id, nivel: (a.control_level as NivelControl | null) ?? null, texto: a.description, de: 'Incidente' }));
  return { deIper, deIncidentes };
}

/** Medida lista para mostrar, con el texto y el seguimiento de su origen (sin copiarlos). */
export function medidaVista(m: FilaMedida, iper: Map<string, IperConMedidas>, acciones: Map<string, AccionIncidente>): MedidaVista {
  if (m.source === 'incidente') {
    const a = m.incident_action_id ? acciones.get(m.incident_action_id) : undefined;
    const cerrada = a?.status === 'completada';
    return { id: m.id, origen: 'incidente', nivel: (a?.control_level as NivelControl | null) ?? null,
      descripcion: a?.description ?? 'Medida del incidente (ya no existe en Incidentes)', responsable: a?.responsible_name ?? '',
      compromiso: a?.due_date ?? null, seguimiento: a?.follow_up ?? '', resultado: '', estado: cerrada ? 'cerrada' : a?.status === 'en_proceso' ? 'en_curso' : 'pendiente',
      cerrada_el: cerrada ? (a?.completed_at ?? (a?.closed_at ? diaEc(a.closed_at) : null)) : null, evidencia: a?.evidence_name ?? null, prioritaria: m.for_priority_group };
  }
  const desc = m.source === 'iper'
    ? String((m.iper_assessment_id && iper.get(m.iper_assessment_id) ? (iper.get(m.iper_assessment_id) as unknown as Record<string, unknown>)[COLUMNA_IPER[m.control_level as NivelControl]] : '') ?? '').trim()
      || 'Medida de la Matriz de Riesgos (la evaluación ya no existe)'
    : (m.description ?? '').trim();
  return { id: m.id, origen: m.source, nivel: m.control_level, descripcion: desc, responsable: m.responsible_name ?? '', compromiso: m.due_date,
    seguimiento: m.follow_up ?? '', resultado: m.result ?? '', estado: m.status, cerrada_el: m.closed_at, evidencia: m.evidence_name, prioritaria: m.for_priority_group };
}

/** GT 7: medidas agrupadas por nivel de la jerarquía de controles, en su orden (las sin nivel, al final). */
export function agruparPorJerarquia(medidas: MedidaVista[]): { nivel: NivelControl | null; medidas: MedidaVista[] }[] {
  const grupos: { nivel: NivelControl | null; medidas: MedidaVista[] }[] = [];
  for (const nivel of [...ORDEN_JERARQUIA, null] as (NivelControl | null)[]) {
    const del = medidas.filter(m => (m.nivel ?? null) === nivel)
      .sort((a, b) => (a.compromiso ?? '9999').localeCompare(b.compromiso ?? '9999') || a.descripcion.localeCompare(b.descripcion, 'es'));
    if (del.length) grupos.push({ nivel, medidas: del });
  }
  return grupos;
}

// ── Contenido congelado ──
export function adjuntoContenido(a: FilaAdjunto): AdjuntoContenido {
  const base: AdjuntoContenido = { id: a.id, tipo: a.kind, titulo: a.title.trim(), archivo: a.file_name };
  if (a.kind === 'laboratorio') {
    return { ...base, agente: a.agent_name, tipo_agente: a.agent_type, resultado: a.result_value === null ? null : Number(a.result_value),
      limite: a.limit_value === null ? null : Number(a.limit_value), unidad: a.unit, fuente_limite: a.limit_source, fecha: a.measured_at,
      cumple: a.result_value === null || a.limit_value === null ? null : Number(a.result_value) <= Number(a.limit_value), proveedor: a.provider };
  }
  if (a.kind === 'calibracion') return { ...base, equipo: a.equipment, serie: a.serial_number, proveedor: a.provider, vigente_hasta: a.valid_until, fecha: a.measured_at };
  return base;
}

export interface EntradaContenido {
  informe: Pick<FilaInforme, 'id' | 'kind' | 'conclusions' | 'recommendations' | 'position_name'>;
  puestoId: string; empresa: string; hoy: Date; expuestos: number; evaluaciones: Evaluacion[]; adjuntos: FilaAdjunto[]; medidas: MedidaVista[];
  /** Solo GT 6: cuántos trabajadores del puesto tienen condición de atención prioritaria (sin nombres ni tipos). */
  prioritarios: number | null;
}
export function contenidoInforme(e: EntradaContenido): ContenidoInforme {
  const tipo = e.informe.kind;
  const evals = evaluacionesDeTipo(tipo, e.evaluaciones).map(({ cambio, ...x }) => x);
  const medidasTipo = tipo === 'medidas' ? e.medidas : tipo === 'riesgos' ? e.medidas.filter(m => m.prioritaria) : [];
  return {
    informe_id: e.informe.id, tipo, empresa: e.empresa, puesto_id: e.puestoId, puesto: e.informe.position_name, fecha_corte: isoDia(e.hoy),
    expuestos: e.expuestos, evaluaciones: tipo === 'medidas' ? [] : evals,
    adjuntos: tipo === 'medidas' ? [] : [...e.adjuntos].sort((a, b) => a.kind.localeCompare(b.kind) || a.id.localeCompare(b.id)).map(adjuntoContenido),
    medidas: [...medidasTipo].sort((a, b) => a.id.localeCompare(b.id)),
    conclusiones: (e.informe.conclusions ?? '').trim(), recomendaciones: (e.informe.recommendations ?? '').trim(),
    atencion_prioritaria: tipo === 'riesgos' && e.prioritarios !== null
      ? { trabajadores: e.prioritarios, medidas: e.medidas.filter(m => m.prioritaria).length } : null,
  };
}

/** Lo que falta para aprobar (vacío = se puede aprobar). */
export function faltantesAprobacion(c: ContenidoInforme): string[] {
  const f: string[] = [];
  if (c.tipo !== 'medidas' && c.evaluaciones.length === 0 && !c.adjuntos.some(a => a.tipo === 'laboratorio'))
    f.push('El puesto no tiene evaluaciones ni mediciones para este informe.');
  if (c.tipo === 'medidas' && c.medidas.length === 0) f.push('El puesto no tiene medidas vinculadas.');
  if (!c.conclusiones) f.push('Escribe las conclusiones.');
  if (!c.recomendaciones) f.push('Escribe las recomendaciones.');
  if (c.tipo === 'riesgos' && c.atencion_prioritaria && c.atencion_prioritaria.trabajadores > 0 && c.atencion_prioritaria.medidas === 0)
    f.push('El puesto tiene trabajadores con condición de atención prioritaria: agrega al menos una medida para ese grupo.');
  return f;
}

// ── Versiones ──
/** La versión aprobada más reciente de un informe (o de un procedimiento). */
export function ultimaVersion<V extends Pick<FilaVersion, 'version'>>(versiones: V[]): V | null {
  return versiones.reduce<V | null>((m, v) => (!m || v.version > m.version ? v : m), null);
}
/** Una versión aprobada nunca se edita: solo se aprueba otra (número siguiente). */
export const puedeEditarVersion = (_v: Pick<FilaVersion, 'id'>) => false;
export const siguienteVersion = (versiones: Pick<FilaVersion, 'version'>[]) => (ultimaVersion(versiones)?.version ?? 0) + 1;

// ── Vigencia y estado de un puesto ──
export type EstadoPuesto = 'vigente' | 'borrador' | 'sin_informe' | 'vencido_reciente' | 'vencido';
export interface ResultadoPuesto { estado: EstadoPuesto; vencidoDesde: string | null; motivo: 'plazo' | 'cambio' | null; vence: string | null; dias: number }

/**
 * Estado del informe de un puesto. Vigente: aprobado hace menos de 12 meses y con la misma huella de evaluaciones.
 * Si cambió una evaluación después de aprobar, queda vencido desde la fecha de ese cambio (si no se puede fechar, desde
 * hoy). Vencido hace 30 días o menos: En proceso; más: No cumple.
 */
export function estadoInformePuesto(tieneBorrador: boolean, version: Pick<FilaVersion, 'approved_at' | 'eval_fingerprint'> | null,
  huellaActual: string, evals: Pick<Evaluacion, 'cambio'>[], hoy: Date): ResultadoPuesto {
  const hoyIso = isoDia(hoy);
  if (!version) return { estado: tieneBorrador ? 'borrador' : 'sin_informe', vencidoDesde: null, motivo: null, vence: null, dias: 0 };
  const aprobado = diaEcuador(version.approved_at);
  const vence = sumarMesesIso(aprobado, MESES_VIGENCIA);
  let desde: string | null = vence <= hoyIso ? vence : null;
  let motivo: 'plazo' | 'cambio' | null = desde ? 'plazo' : null;
  if (version.eval_fingerprint !== huellaActual) {
    const posteriores = evals.map(e => e.cambio).filter(c => c > version.approved_at).sort();
    const cambio = posteriores.length ? diaEcuador(posteriores[0]) : hoyIso;
    if (!desde || cambio < desde) { desde = cambio; motivo = 'cambio'; }
  }
  if (!desde) return { estado: 'vigente', vencidoDesde: null, motivo: null, vence, dias: 0 };
  const dias = Math.max(0, diasEntre(desde, hoyIso));
  return { estado: dias <= DIAS_TOLERANCIA ? 'vencido_reciente' : 'vencido', vencidoDesde: desde, motivo, vence, dias };
}

export const ETIQUETA_ESTADO_PUESTO: Record<EstadoPuesto, string> = {
  vigente: 'Aprobado y vigente', borrador: 'En borrador', sin_informe: 'Sin informe', vencido_reciente: 'Vencido (en tolerancia)', vencido: 'Vencido',
};
